"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeOrderPrice = exports.formatProductPrice = exports.formatPrice = exports.formatProductQuantity = exports.computeProductPrice = exports.refundAmounts = exports.shippingAmountFromDeliveryPrices = exports.selectDeliveryPricing = exports.computeShippingAmount = exports.getCouponPromotionValue = exports.getPercentPromotionValue = exports.computePromotionalPriceForQuantity = exports.getPromotionalPrice = void 0;
const utils_1 = require("./utils");
var utils_2 = require("./utils");
Object.defineProperty(exports, "getPromotionalPrice", { enumerable: true, get: function () { return utils_2.getPromotionalPrice; } });
Object.defineProperty(exports, "computePromotionalPriceForQuantity", { enumerable: true, get: function () { return utils_2.computePromotionalPriceForQuantity; } });
Object.defineProperty(exports, "getPercentPromotionValue", { enumerable: true, get: function () { return utils_2.getPercentPromotionValue; } });
Object.defineProperty(exports, "getCouponPromotionValue", { enumerable: true, get: function () { return utils_2.getCouponPromotionValue; } });
/**
 * Computes the shipping amount before coupons taking into account deltaDay, deltaSlot and deliveryFixedFees.
 * It cannot be lower than 0.
 * When based shipping amount reached 0, a positive deltaSlot is reduced to 0.
 * Returns also the `deltaSlotCharged` and the `deltaDayCharged` computed without `deliveryFixedFees`.
 * @public
 */
function computeShippingAmount({ deltaDay, deltaSlot, shippingAmount, deliveryFixedFees, }) {
    const deltaSlotReal = Math.round(shippingAmount > 0 || deltaSlot < 0 ? deltaSlot : 0);
    const shippingBeforeFixedFees = Math.max(Math.round(shippingAmount + deltaDay + deltaSlotReal), 0);
    // delta day charged cannot lead the shipping amount to be lower than 0
    const deltaDayCharged = Math.round(deltaDay < 0 && shippingAmount < Math.abs(deltaDay)
        ? Math.max(deltaDay, 0 - shippingAmount - Math.max(deltaSlotReal, 0))
        : deltaDay);
    const deltaSlotCharged = Math.round(shippingBeforeFixedFees - shippingAmount - deltaDayCharged);
    const shippingBeforeCoupons = Math.max(0, Math.round(shippingBeforeFixedFees + deliveryFixedFees));
    return {
        deltaDayCharged,
        deltaSlotCharged,
        shippingBeforeCoupons,
    };
}
exports.computeShippingAmount = computeShippingAmount;
/**
 * Select the delivery pricing from the given `deliveryPrices` according to the given `beforeShippingPrice`
 * @param deliveryPrices - the given `deliveryPrices` configuration
 * @param beforeShippingPrice - the net price of the cart before shipping
 * @throws Error - in case of no delivery pricing found for given `beforeShippingPrice`
 * @public
 */
function selectDeliveryPricing(deliveryPrices, beforeShippingPrice) {
    const pricing = (0, utils_1.sortBy)(deliveryPrices, p => p.minCartNetPrice, true).find(p => p.minCartNetPrice <= beforeShippingPrice);
    if (!pricing) {
        throw new Error(`No pricing for amount ${beforeShippingPrice}. Config: ${JSON.stringify(deliveryPrices)}`);
    }
    return pricing;
}
exports.selectDeliveryPricing = selectDeliveryPricing;
/**
 * Computes the shipping amount before and after coupons corresponding to given beforeShippingPrice, taking into account deltaDay, deltaSlot and deliveryFixedFees.
 * They cannot be lower than 0.
 * Returns also the charged delta slot and day used in computation:
 * - deltaSlot follows rules used in computeShippingAmount function: reduced to 0 if positive and based shipping amount is 0.
 * - A negative deltaDay is charged to the maximum of the opposite of based shipping amount + a positive deltaSlot.
 * - deltaSlot is charged to the difference of shipping amount before coupons with based shipping amount and delta slot charged.
 * - in case of free shipping coupons, shipping amount after coupons is deliveryFixedFees (if positive) and no deltas are charged.
 * @public
 */
function shippingAmountFromDeliveryPrices(order, beforeShippingPrice, hasFreeShippingCoupon) {
    const { deliveryPrices, deltaDay, deltaSlot, deliveryFixedFees } = order;
    const pricing = selectDeliveryPricing(deliveryPrices, beforeShippingPrice);
    const { shippingBeforeCoupons, deltaDayCharged, deltaSlotCharged } = computeShippingAmount({
        deltaDay,
        deltaSlot,
        shippingAmount: pricing.shippingAmount,
        deliveryFixedFees,
    });
    if (hasFreeShippingCoupon) {
        return {
            shippingBeforeCoupons,
            shippingAfterCoupons: Math.max(0, Math.round(deliveryFixedFees)),
            deltaDayCharged: 0,
            deltaSlotCharged: 0,
        };
    }
    return {
        shippingBeforeCoupons,
        shippingAfterCoupons: shippingBeforeCoupons,
        deltaDayCharged,
        deltaSlotCharged,
    };
}
exports.shippingAmountFromDeliveryPrices = shippingAmountFromDeliveryPrices;
/**
 * compute the amount to refund (when we want to refund an order article) according to params.
 *
 * @public
 */
const refundAmounts = (params) => {
    var _a, _b, _c, _d, _e, _f, _g;
    // isRefundedByPrepay true means that the refund comes from a J+7 refund, we do not take it into account
    const alreadyRefunded = ((_a = params.refunded) !== null && _a !== void 0 ? _a : [])
        .filter(r => !r.isRefundedByPrepay)
        .reduce((acc, r) => acc + r.net, 0);
    if ('weight' in params && 'count' in params) {
        throw new Error('only one of count or weight need to be set to a positive value');
    }
    if ((_b = params.pricing.perWeightUnit) === null || _b === void 0 ? void 0 : _b.main) {
        if (!('weight' in params)) {
            throw new Error('perWeightUnit is main price, so weight is needed');
        }
        if (params.weight && params.weight < 0) {
            throw new Error('weight needs to be set to a positive value');
        }
        const maxNet = ((_d = (_c = params.real) === null || _c === void 0 ? void 0 : _c.price.net) !== null && _d !== void 0 ? _d : params.quotation.price.preauthorizedAmount) -
            alreadyRefunded;
        const theoreticalNet = params.pricing.perWeightUnit.net * params.weight;
        const net = Math.round(Math.min(maxNet, theoreticalNet));
        return {
            net,
            vat: Math.round(net - net / (1 + params.quotation.price.vatRate)),
            weight: params.weight,
            count: Math.round(params.weight / params.pricing.perWeightUnit.value),
        };
    }
    if (!('count' in params)) {
        throw new Error('perGranularity is main price, so count is needed');
    }
    if (params.count && params.count < 0) {
        throw new Error('count needs to be set to a positive value');
    }
    const maxNet = ((_f = (_e = params.real) === null || _e === void 0 ? void 0 : _e.price.net) !== null && _f !== void 0 ? _f : params.quotation.price.preauthorizedAmount) -
        alreadyRefunded;
    const theoreticalNet = params.pricing.perGranularity.net * params.count;
    const net = Math.round(Math.min(maxNet, theoreticalNet));
    return {
        net,
        vat: Math.round(net - net / (1 + params.quotation.price.vatRate)),
        count: params.count,
        weight: ((_g = params.pricing.perWeightUnit) === null || _g === void 0 ? void 0 : _g.value)
            ? params.pricing.perWeightUnit.value * params.count
            : undefined,
    };
};
exports.refundAmounts = refundAmounts;
/**
 * @public
 */
const computeProductPrice = (product, coupons, perWeightMargin, config, promo) => {
    var _a, _b, _c, _d, _e, _f;
    /** free qty / total qty, between 0 and 1 */
    let freeRatio;
    if (config.autoDetectCoupons) {
        const freeArticleCoupons = coupons.flatMap(c => c.type === 'FREE_ARTICLE' && c.article.articleId === product.articleId
            ? [c]
            : []);
        const freeCount = (0, utils_1.sumBy)(freeArticleCoupons, c => c.article.quantity);
        if (freeCount > product.count) {
            throw new Error("Free quantity cannot exceed product's quantity");
        }
        if (product.count <= 0) {
            throw new Error('Cannot use autodetectCoupons if count is 0');
        }
        freeRatio = freeCount / product.count;
    }
    else {
        // When in autoDetecCoupons: false, each line is either fully paid or fully free (see the jsdoc above)
        freeRatio = product.freeArticleCouponId ? 1 : 0;
    }
    // a convoluted way to set the weight and the pricing source (ie whether we should compute the price
    //from the weight or count), but that allows us to encode in the types that 'weight' products always have a weight
    let weightAndSource;
    if ((_a = product.pricing.perWeightUnit) === null || _a === void 0 ? void 0 : _a.main) {
        const weight = (_b = product.weight) !== null && _b !== void 0 ? _b : product.count * product.pricing.perWeightUnit.value;
        weightAndSource = {
            weight,
            source: 'weight',
            perWeightUnit: product.pricing.perWeightUnit,
        };
    }
    else {
        const weight = (_c = product.weight) !== null && _c !== void 0 ? _c : (product.pricing.perWeightUnit
            ? product.count * product.pricing.perWeightUnit.value
            : undefined);
        weightAndSource = {
            weight,
            source: 'count',
        };
    }
    let theoreticalNet, paidWeight;
    if (weightAndSource.source === 'weight') {
        paidWeight = weightAndSource.weight * (1 - freeRatio);
        theoreticalNet = (0, utils_1.round)(weightAndSource.perWeightUnit.net * paidWeight, 2);
    }
    else {
        paidWeight =
            weightAndSource.weight !== undefined
                ? weightAndSource.weight * (1 - freeRatio)
                : undefined;
        theoreticalNet = (0, utils_1.round)(product.pricing.perGranularity.net * product.count * (1 - freeRatio), 2);
    }
    let itemPrice = product.pricing.perGranularity.net;
    if (promo) {
        const afterPromo = (0, utils_1.computePromotionalPriceForQuantity)(product.pricing.perGranularity.net, promo, product.count);
        theoreticalNet = (0, utils_1.round)(afterPromo.total, 2);
        if (!promo.conditions.nthQuantity) {
            itemPrice = (0, utils_1.round)(afterPromo.unitPrice, 2);
        }
    }
    const net = Math.min(config.mode === 'real' ? config.preauthorization : Infinity, theoreticalNet);
    const dutyFree = (0, utils_1.round)(net / (1 + product.vatRate), 2);
    const vat = (0, utils_1.round)(net - dutyFree, 2);
    const preauthorization = Math.round(net * (((_d = product.pricing.perWeightUnit) === null || _d === void 0 ? void 0 : _d.main) ? 1 + perWeightMargin : 1));
    return {
        articleId: product.articleId,
        count: {
            quantity: product.count,
            freeQuantity: Math.round(product.count * freeRatio),
            itemDefinition: product.pricing.perGranularity.unit !== 'count' &&
                product.pricing.perWeightUnit
                ? {
                    type: 'arbitraryQuantity',
                    weight: {
                        value: product.pricing.perWeightUnit.value,
                        unit: product.pricing.perWeightUnit.unit,
                    },
                }
                : product.pricing.perWeightUnit
                    ? {
                        type: 'pieceWeight',
                        terminology: (_e = product.granularityTerminology) !== null && _e !== void 0 ? _e : {
                            singular: 'pièce',
                            plural: 'pièces',
                        },
                        weight: {
                            value: product.pricing.perWeightUnit.value,
                            unit: product.pricing.perWeightUnit.unit,
                        },
                    }
                    : {
                        type: 'piece',
                        terminology: (_f = product.granularityTerminology) !== null && _f !== void 0 ? _f : {
                            singular: 'pièce',
                            plural: 'pièces',
                        },
                    },
            itemPrice,
        },
        ...(weightAndSource.weight !== undefined &&
            paidWeight !== undefined &&
            product.pricing.perWeightUnit
            ? {
                weight: {
                    weight: weightAndSource.weight,
                    freeWeight: weightAndSource.weight - paidWeight,
                    unitPrice: product.pricing.perWeightUnit.net,
                    unit: product.pricing.perWeightUnit.unit,
                },
            }
            : {}),
        totals: {
            vatRate: product.vatRate,
            netBeforeCoupons: (0, utils_1.round)(net),
            dutyFree: (0, utils_1.round)(dutyFree),
            vat: (0, utils_1.round)(vat),
            theoreticalNet: (0, utils_1.round)(theoreticalNet),
            net: (0, utils_1.round)(net),
            preauthorization: (0, utils_1.round)(preauthorization),
            source: weightAndSource.source,
        },
    };
};
exports.computeProductPrice = computeProductPrice;
/**
 * @alpha
 */
const formatProductQuantity = (quantity, itemDef) => {
    let u;
    switch (itemDef.type) {
        case 'pieceWeight':
            u = `${quantity <= 1
                ? itemDef.terminology.singular
                : itemDef.terminology.plural} de ${itemDef.weight.value} ${itemDef.weight.unit}`;
            break;
        case 'piece':
            u =
                quantity <= 1
                    ? itemDef.terminology.singular
                    : itemDef.terminology.plural;
            break;
        case 'arbitraryQuantity':
            u = `x ${itemDef.weight.value} ${itemDef.weight.unit}`;
            break;
    }
    return `${quantity} ${u}`;
};
exports.formatProductQuantity = formatProductQuantity;
/**
 * @internal
 */
const formatPrice = (value) => `${(value / 100).toFixed(2)} €`;
exports.formatPrice = formatPrice;
/**
 * @alpha
 */
const formatProductPrice = (p, mode) => ({
    quantity: (0, exports.formatProductQuantity)(p.count.quantity, p.count.itemDefinition),
    freeQuantity: (0, exports.formatProductQuantity)(p.count.freeQuantity, p.count.itemDefinition),
    unitPrice: (0, exports.formatPrice)(p.count.itemPrice),
    weight: p.weight && JSON.stringify(p.weight),
    source: p.totals.source,
    dutyFree: (0, exports.formatPrice)(p.totals.dutyFree),
    vat: (0, exports.formatPrice)(p.totals.vat),
    netBeforeCoupons: (0, exports.formatPrice)(p.totals.netBeforeCoupons),
    ...(mode === 'real' && {
        theoreticalNet: (0, exports.formatPrice)(p.totals.theoreticalNet),
    }),
    net: (0, exports.formatPrice)(p.totals.net),
    ...(mode === 'quotation' && {
        preauthorization: (0, exports.formatPrice)(p.totals.preauthorization),
    }),
});
exports.formatProductPrice = formatProductPrice;
const sanitizeOrder = (order) => ({
    ...order,
    deliveryPrices: order.deliveryPrices.map(p => ({
        ...p,
        shippingAmount: Math.round(p.shippingAmount),
    })),
});
/**
 * Get price quotation from products, time slots and currency
 * Returns a price quotation object
 *
 * @public
 */
function computeOrderPrice(_order, options) {
    const order = sanitizeOrder(_order);
    const shippingVatRate = 0.2;
    const preparationVatRate = 0.2;
    const pPrices = order.products.map((p, i) => {
        if (options.mode === 'quotation') {
            return (0, exports.computeProductPrice)(p, _order.coupons, order.perWeightUnitMargin, {
                mode: 'quotation',
                autoDetectCoupons: options.autoDetectCoupons,
            });
        }
        return (0, exports.computeProductPrice)(p, _order.coupons, order.perWeightUnitMargin, {
            mode: 'real',
            preauthorization: options.quotationPreauthorization[i],
            autoDetectCoupons: options.autoDetectCoupons,
        });
    });
    const products = {
        dutyFree: (0, utils_1.sumOfRounded)(pPrices.map(p => p.totals.dutyFree)),
        net: (0, utils_1.sumOfRounded)(pPrices.map(p => p.totals.net)),
        vat: (0, utils_1.sumOfRounded)(pPrices.map(p => p.totals.vat)),
        vatByRate: Object.entries((0, utils_1.groupBy)(pPrices, p => p.totals.vatRate)).map(([rate, ps]) => [
            Number(rate),
            (0, utils_1.sumOfRounded)(ps.map(p => p.totals.vat)),
        ]),
    };
    const hasFreeShippingCoupon = order.coupons.some(c => c.type === 'FREE_SHIPPING');
    const { shippingBeforeCoupons, shippingAfterCoupons, deltaDayCharged, deltaSlotCharged, } = shippingAmountFromDeliveryPrices(order, products.net, hasFreeShippingCoupon);
    const shipping = {
        netBeforeCoupons: shippingBeforeCoupons,
        net: shippingAfterCoupons,
        vat: Math.round(shippingAfterCoupons - shippingAfterCoupons / (1 + shippingVatRate)),
        vatRate: shippingVatRate,
        deltaDayCharged,
        deltaSlotCharged,
    };
    const preparation = order.preparationFee && order.products.length > 0
        ? {
            net: order.preparationFee,
            vat: Math.round(order.preparationFee -
                order.preparationFee / (1 + preparationVatRate)),
            vatRate: preparationVatRate,
        }
        : {
            net: 0,
            vat: 0,
            vatRate: preparationVatRate,
        };
    const total = {
        net: products.net + shipping.net + preparation.net,
        vat: products.vat + shipping.vat + preparation.vat,
    };
    const valueCoupons = (0, utils_1.sumBy)(order.coupons, c => (0, utils_1.getCouponPromotionValue)({
        articles: pPrices.map(p => ({
            articleId: p.articleId,
            net: p.totals.net,
        })),
        coupon: c,
    }));
    const payments = {
        valueCoupons,
        creditCard: Math.max(total.net - valueCoupons, 0),
    };
    const preauthorization = Math.max((0, utils_1.sumOfRounded)(pPrices.map(p => p.totals.preauthorization)) +
        shipping.net +
        preparation.net -
        valueCoupons, 0);
    return { products, shipping, total, payments, preauthorization, preparation };
}
exports.computeOrderPrice = computeOrderPrice;
